import React, { useEffect, useState } from "react";
import ReactTable from "react-table";
import _ from "lodash";

import ReactTablePagination from "../task-list/components/pagination";

import { getStorageValue, setStorageValue } from "../../shared/storage";
import { useWindowVisible } from "./lib/hooks";

var debounceTimer = null;
var pollTimer = null;
var lastAsyncCall = null;

const DEFAULT_SETTINGS_VALUE = "task-list-settings";

const useStoredOrDefault = (storedKey, defaultValue) => {
    if (!storedKey) {
        return defaultValue;
    }

    const storedValue = getStorageValue(storedKey);

    return storedValue ? storedValue : defaultValue;
};

const defaultResults = {
    data: [],
    page: 1,
    pages: -1,
    totalRecords: 0,
};

const PaginatedTable = (props) => {
    const storedSettings = getStorageValue(DEFAULT_SETTINGS_VALUE) || {};
    const storedSort = useStoredOrDefault(props.storageSortValue, props.settings.sorted);
    const storedFilter = useStoredOrDefault(props.storageFilterValue, props.settings.filtered);
    const storedPageSize = useStoredOrDefault(props.storagePageSizeValue, props.settings.pageSize);
    const useSettings = {
        ...props.settings,
        ...storedSettings,
        pageSize: !props.storagePageSizeValue ? (storedSettings.pageSize ? storedSettings.pageSize : props.settings.pageSize) : storedPageSize,
        sorted: storedSort,
        filtered: storedFilter,
    };
    const [state, setState] = useState(useSettings);
    const [results, setResults] = useState({ ...defaultResults });
    const isWindowVisible = useWindowVisible();

    useEffect(() => {
        async function fetchData(lastCall) {
            if (!isWindowVisible) {
                return;
            }

            const data = await props.getData(state.page, state.pageSize, state.sorted, state.filtered);

            if (lastAsyncCall !== lastCall) {
                return;
            }

            setResults({
                data: _.compact(data.items),
                page: data.currentPage,
                pages: data.totalPageCount,
                totalRecords: data.totalRecordCount,
            });

            if (props.getDataCallback) {
                props.getDataCallback({
                    data: _.compact(data.items),
                    page: data.currentPage,
                    pages: data.totalPageCount,
                    totalRecords: data.totalRecordCount,
                });
            }

            if (props.pollTimeout && isWindowVisible) {
                pollTimer = setTimeout(() => {
                    lastAsyncCall = new Date().getTime();
                    fetchData(lastAsyncCall);
                }, props.pollTimeout);
            }
        }

        clearTimeout(debounceTimer);
        clearTimeout(pollTimer);
        debounceTimer = setTimeout(() => {
            clearTimeout(debounceTimer);
            clearTimeout(pollTimer);
            lastAsyncCall = new Date().getTime();
            fetchData(lastAsyncCall);
        }, 700);

        return () => {
            clearTimeout(pollTimer);
            clearTimeout(debounceTimer);
        };
    }, [state.page, state.pageSize, state.filtered, state.sorted, props.updatePulse, isWindowVisible]);

    useEffect(() => {
        if (props.appendSettings) {
            setState({
                ...state,
                ...props.appendSettings,
            });
        }
    }, [props.appendSettings]);

    const onPageChange = (currentPage) => {
        setState({
            ...state,
            page: currentPage + 1,
        });
    };
    const onPageSizeChange = (pageSize) => {
        setState({
            ...state,
            page: 1,
            pageSize: pageSize,
        });

        if (props.storagePageSizeValue) {
            setStorageValue(props.storagePageSizeValue, pageSize);
        } else {
            setStorageValue(DEFAULT_SETTINGS_VALUE, { pageSize: pageSize });
        }
    };
    const onSortedChange = (sort) => {
        setState({
            ...state,
            page: 1,
            sorted: sort,
        });

        if (props.storageSortValue) {
            setStorageValue(props.storageSortValue, sort);
        }
    };
    const onFilteredChange = (filter, column) => {
        setState({
            ...state,
            filtered: filter,
            page: 1,
        });

        if (props.storageFilterValue) {
            setStorageValue(props.storageFilterValue, filter);
        }
    };

    return (
        <div>
            <ReactTable
                manual
                {...results}
                page={results.page - 1}
                pageSize={state.pageSize}
                sorted={state.sorted}
                filtered={state.filtered}
                filterable={props.filterable}
                sortable={props.sortable}
                columns={props.columns}
                minRows="5"
                multiSort={props.multiSort}
                showPagination={true}
                PaginationComponent={ReactTablePagination}
                getTrProps={props.onTaskRowClick}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                onSortedChange={onSortedChange}
                onFilteredChange={onFilteredChange}
            />
        </div>
    );
};

PaginatedTable.defaultProps = {
    appendSettings: null,
    settings: {
        page: 1,
        pageSize: 20,
        sorted: [],
        filtered: [],
    },
    minRows: 10,
    multiSort: true,
    filterable: true,
    pollTimeout: null,
    sortable: true,
    storageSortValue: null,
    storageFilterValue: null,
    storageSettingsValue: "task-list-settings",
};

export default PaginatedTable;
